import React, { useState } from "react";
import { Calendar, X, Check, Coffee } from "@phosphor-icons/react";
import { teleproApi } from "../lib/teleproApi";

/**
 * Bloc absence — le téléprospecteur déclare son indisponibilité pour
 * 1 ou plusieurs jours. Les prospects assignés aujourd'hui sont libérés
 * automatiquement (ils retourneront dans le pool dès demain).
 *
 * Affiche aussi le statut courant : si absent_until est défini et >= today,
 * une bannière orange l'indique avec un bouton "Reprendre maintenant".
 */
export default function TeleproAbsenceCard({ me, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [until, setUntil] = useState(todayIso());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const isAbsent = me?.absent_until && me.absent_until >= todayIso();

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const res = await teleproApi.post("/me/absence", { until });
      onUpdated?.(res.data.telepro);
      setOpen(false);
      const n = res.data.released_prospects || 0;
      if (n > 0) {
        alert(`Absence enregistrée jusqu'au ${formatFr(until)}. ${n} prospects libérés — ils seront redistribués dès demain.`);
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossible d'enregistrer l'absence.");
    } finally {
      setBusy(false);
    }
  };

  const resume = async () => {
    if (!window.confirm("Reprendre tes appels immédiatement ? Tu redeviendras éligible aux listes du lendemain matin.")) return;
    setBusy(true); setErr("");
    try {
      const res = await teleproApi.post("/me/absence", { until: null });
      onUpdated?.(res.data.telepro);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Impossible de reprendre.");
    } finally {
      setBusy(false);
    }
  };

  if (isAbsent) {
    return (
      <div className="bg-secondary/10 border-l-4 border-secondary rounded-sm p-4 mb-6" data-testid="telepro-absent-banner">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="w-9 h-9 bg-secondary text-secondary-foreground grid place-items-center rounded-sm shrink-0">
            <Coffee size={18} weight="duotone"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-sm sm:text-base text-secondary mb-0.5">
              Tu es noté(e) absent(e) jusqu'au {formatFr(me.absent_until)}
            </div>
            <p className="text-xs sm:text-[13px] text-foreground/85 leading-relaxed">
              Aucune liste ne te sera envoyée pendant cette période. Les prospects
              du jour ont été remis dans le pool pour ne pas être perdus.
            </p>
          </div>
          <button
            onClick={resume}
            disabled={busy}
            className="bg-card border border-secondary text-secondary hover:bg-secondary hover:text-secondary-foreground text-xs font-semibold px-3 py-2 rounded-sm inline-flex items-center gap-1 transition disabled:opacity-50"
            data-testid="telepro-resume-now"
          >
            <Check size={14} weight="bold"/> Reprendre maintenant
          </button>
        </div>
      </div>
    );
  }

  if (!open) {
    return (
      <div className="mb-6 flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={() => setOpen(true)}
          className="text-xs text-muted-foreground hover:text-secondary inline-flex items-center gap-1.5 border border-dashed border-border hover:border-secondary rounded-sm px-3 py-2 transition"
          data-testid="telepro-declare-absence"
        >
          <Coffee size={14} weight="duotone"/> Je serai absent(e) — déclarer une indisponibilité
        </button>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-sm p-4 mb-6" data-testid="telepro-absence-form">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Calendar size={16} weight="duotone" className="text-secondary"/>
          <h3 className="font-display font-medium text-sm">Déclarer une absence</h3>
        </div>
        <button onClick={() => setOpen(false)} className="text-muted-foreground hover:text-foreground" aria-label="Fermer">
          <X size={16} weight="bold"/>
        </button>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Choisis la date jusqu'à laquelle tu seras absent(e) (incluse). Les prospects
        assignés aujourd'hui seront libérés et redistribués.
      </p>
      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Absent jusqu'au</label>
          <input
            type="date"
            min={todayIso()}
            value={until}
            onChange={(e) => setUntil(e.target.value)}
            className="w-full bg-card border border-border rounded-sm px-3 py-2 text-sm focus:outline-none focus:border-secondary"
            data-testid="absence-until-input"
          />
        </div>
        <button
          onClick={submit}
          disabled={busy || !until}
          className="bg-secondary text-secondary-foreground hover:bg-[#a35d42] px-4 py-2 rounded-sm text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="absence-confirm"
        >
          <Check size={14} weight="bold"/> {busy ? "…" : "Confirmer"}
        </button>
      </div>
      {err && <p className="text-xs text-destructive mt-2" data-testid="absence-error">{err}</p>}
    </div>
  );
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function formatFr(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}
